import Model from './model.js';
import Company from './company.js';
import Address from './address.js';
import BankAccount from './bank-account.js';
import User from './user.js';
import CompanyLocation from './company-location.js';
import TimeEntry from './time-entry.js';
import Position from './position.js';
import EmployeeRemoval from './employee-removal.js';
import Attachments from 'newCollections/attachments.js';
import Attachment from 'newModels/attachment.js';
import Payslips from 'newCollections/payslips.js';
import Payroll from 'newModels/payroll.js';
import LeaveApprovals from 'newCollections/leave-approvals.js';
import ItemYearlyTotals from 'newCollections/item-yearly-totals.js';
import countriesList from 'helpers/countriesList';
import moment from 'moment';
import axios from 'axios';
import Log from 'lib/log';
import config from 'config';
const log = Log('newModels/employee');

const FORMS_URL = config.apiUrl + `/GovernmentForms`;

// TODO We don't have the time to go down the rabbit hole of getting rid
// of these "consts" and constValue, but we have to eventually.
const constValues = (re) => {
    return _.map(_.filter(_.keys(consts), function(constName) {
        return re.test(constName);
    }), function(constName) {
        return consts[constName];
    });
};
export const consts = {
    GENDER_MALE:                          1,
    GENDER_FEMALE:                        2,

    MARRIED_STATUS_DEFAULT:               1,
    MARRIED_STATUS_SINGLE:                1,
    MARRIED_STATUS_SINGLE_WITH_CHILDREN:  2,
    MARRIED_STATUS_DIVORCED:              3,
    MARRIED_STATUS_SPOUSE_WORKING:        4,
    MARRIED_STATUS_SPOUSE_NOT_WORKING:    5,

    RESIDENT_SUBTYPE_NORMAL:              0,
    RESIDENT_SUBTYPE_KNOWLEDGE_WORKER:    1,
    RESIDENT_SUBTYPE_REP:                 2,
    RESIDENT_SUBTYPE_FREELANCER:          3,
    RESIDENT_SUBTYPE_PART_TIME:           4,
    RESIDENT_SUBTYPE_DEFAULT:             0,

    ID_TYPE_DEFAULT:                      1,
    ID_TYPE_IC_NUMBER:                    1,
    ID_TYPE_PASSPORT:                     2,

    RACE_DEFAULT:                         0,
    RACE_NONE:                            1,
    RACE_MALAY:                           1,
    RACE_CHINESE:                         2,
    RACE_INDIAN:                          3,
    RACE_OTHER:                           4,

    EARNINGS_FREQUENCY_DEFAULT:           3,
    EARNINGS_FREQUENCY_HOURLY:            1,
    EARNINGS_FREQUENCY_DAILY:             2,
    EARNINGS_FREQUENCY_MONTHLY:           3,

    EPF_EMPLOYER_RATE_DEFAULT:            1,
    EPF_EMPLOYER_RATE_GOVT_TABLE:         1,
    EPF_EMPLOYER_RATE_5RM:                2,

    SOCSO_CATEGORY_DEFAULT:               1,
    SOCSO_CATEGORY_NONE:                  0,
    SOCSO_CATEGORY_INJURY_AND_INVALIDITY: 1,
    SOCSO_CATEGORY_INJURY:                2,

    PAYMENT_METHOD_DEFAULT:               0,
    PAYMENT_METHOD_NONE:                  0,
    PAYMENT_METHOD_BANK_TRANSFER:         1,
    PAYMENT_METHOD_CASH:                  2,
    PAYMENT_METHOD_CHEQUE:                3
};

export default class Employee extends Model {
    urlRoot() { return '/Employees'; }

    url() {
        if (this.user && !this.get(this.idAttribute)) {
            return `${this.user.url()}/employee`;
        }

        return super.url();
    }

    constructor() {
        super(...arguments);
        this.fieldsAffectingProgress = [
            'fullName', 'idStr', 'dob', 'earningsAmount', 'earningsFrequency',
            'commencementDate'
        ];
        this.companyLocation = null;
        this.position = null;
        this.leaveApprovals = new LeaveApprovals(null, {
            requester: this, pagination: false
        });
        this.hasPrevItemYearlyTotals = false;
        this.isIntern = null;
    }

    constraints() {
        return {
            companyId: {presence: true},
            gender: {
                presence: true,
                inclusion: constValues(/^GENDER_/)
            },
            fullName: {
                presence: {message: '^Full Name cannot be blank'}
            },
            email: {
                email: true
            },
            residentSubType: {
                presence: true,
                inclusion: constValues(/^RESIDENT_SUBTYPE_/)
            },
            idType: {
                presence: true,
                inclusion: constValues(/^ID_TYPE_/)
            },
            idStr: {
                presence: true,
                isValidIdStr: true
            },
            race: {
                inclusion: constValues(/^RACE_/)
            },
            nationality: {presence: true},
            marriedStatus: {
                presence: true,
                inclusion: constValues(/^MARRIED_STATUS_/)
            },
            pcbDeductionForChildren: {
                numericality: {
                    notLessThan: 0,
                    notGreaterThan: 100
                }
            },
            dob: {
                presence: {
                    message: (
                        value, attribute, validatorOptions, attributes
                    ) => {
                        if (attributes.idType === 1) {
                            return '^Date of Birth on IC number is invalid';
                        } else {
                            return '^Date of Birth cannot be blank';
                        }
                    }
                }
            },
            earningsFrequency: {
                inclusion: constValues(/^EARNINGS_FREQUENCY_/)
            },
            socsoCategory: {inclusion: constValues(/^SOCSO_CATEGORY_/)},
            ssfwNumber: {
                format: {
                    pattern: /^\d{12}$/,
                    message: '^SSFW Number must be 12 digits long'
                }
            },
            // personalEmail: {email: true},
            preferredPaymentMethod: {
                inclusion: constValues(/^PAYMENT_METHOD_/)
            },
            emergencyContactPhone: {format: {
                pattern: '[0-9\\(\\)\\+\\- ]+',
                message: 'Emergency contact phone must be a valid phone number'
            }}
        };
    } // eof constraints. TODO Move to a separate file?

    defaults() {
        return {
            gender:                    consts.GENDER_MALE,
            isResident:                true,
            residentSubType:           consts.RESIDENT_SUBTYPE_DEFAULT,
            idType:                    consts.ID_TYPE_DEFAULT,
            race:                      consts.RACE_DEFAULT,
            nationality:               'MY',
            isPermanentResident:       false,
            marriedStatus:             consts.MARRIED_STATUS_DEFAULT,
            isDisabled:                false,
            isSpouseDisabled:          false,
            earningsAmount:            0,
            earningsFrequency:         consts.EARNINGS_FREQUENCY_DEFAULT,
            positionId:                null,
            managerId:                 null,
            companyLocationId:         null,
            bankAccountId:             null,
            addressId:                 null,
            children:                  [],
            socsoCategory:             consts.SOCSO_CATEGORY_DEFAULT,
            ssfwNumber:                null,
            isEpf:                     false,
            isEpfMemberBefore1998:     false,
            epfAdditionalRate:         0,
            epfEmployerRate:           consts.EPF_EMPLOYER_RATE_DEFAULT,
            epfEmployerAdditionalRate: 0,
            mtdBorneByEmployer:        false,
            isEis:                     true,
            commencementDate:          null,
            preferredPaymentMethod:    consts.PAYMENT_METHOD_DEFAULT,
            isSetUp:                   false,
            hasPayslips:               false,
            hasValidItemYearlyTotals:  false
        };
    }

    initialize({user = null, company = null, address = null}) {
        this.idAttribute = 'objectId';
        super.initialize(...arguments);

        this.user = user;
        this.company = company;
        this.address = address;
        this.imageChanged = false;
        this.locationAdded = false;
        this.on('change:earningsAmount', this.updatePositionInfo, this);
        this.on('change:earningsFrequency', this.updatePositionInfo, this);
        this.on('change:idType', this.onIdTypeChange, this);
        this.on('change:isResident', this.updateEPFEmployerRate, this);
        this.on('change:isPermanentResident', this.onUpdate, this);
        this.on('change:nationality', this.onUpdate, this);
        this.on('change:dob', this.onUpdate, this);

        return this;
    }

    fetchCompany() {
        if (this.company) {
            return Promise.resolve(this.company);
        }
        const company = new Company({objectId: this.get('companyId')});
        return company.fetch()
            .then(model => {
                this.company = model;
                return this.company;
            });
    }

    fetchUser() {
        if (this.user) {
            Promise.resolve(this.user);
        }

        if (!this.get('userId')) {
            return Promise.resolve(null);
        }

        return new User({id: this.get('userId')})
            .fetch()
            .then(model => {
                this.user = model;
                return this.user;
            });
    }

    fetchRemoval() {
        return EmployeeRemoval.getForEmployee(this)
            .then(employeeRemoval => {
                this.removal = employeeRemoval;
                return this.removal;
            });
    }

    fetchCompanyLocation() {
        if (this.companyLocation) {
            Promise.resolve(this.companyLocation);
        }

        if (!this.get('companyLocationId')) {
            return Promise.resolve(null);
        }

        const companyLocation = app.companyLocations &&
            app.companyLocations.find(
                location => location.id === this.get('companyLocationId')
            );
        if (companyLocation) {
            this.companyLocation = companyLocation;
            return Promise.resolve(this.companyLocation);
        }

        return new CompanyLocation(
            {objectId: this.get('companyLocationId')}
        ).fetch({validate: false})
            .then(model => {
                this.companyLocation = model;
                return this.companyLocation;
            });
    }

    getCompanyLocation() {
        return this.companyLocation;
    }

    fetchPosition() {
        if (this.position) {
            Promise.resolve(this.position);
        }

        if (!this.get('positionId')) {
            return Promise.resolve(null);
        }

        const position = app.positions.find(
            p => p.get('objectId') === this.get('positionId')
        );

        if (position) {
            this.position = position;
            return Promise.resolve(position);
        }

        return new Position(
            {objectId: this.get('positionId')},
            {validate: false}
        ).fetch()
            .then(model => {
                this.position = model;
                return this.position;
            });
    }

    getPosition() {
        return this.position;
    }

    setPosition(position) {
        if (position === null) {
            this.set('positionId', null);
            this.position = null;
        } else if (position.isNew()) {
            this.set('positionId', null);
            this.position = position;
        } else {
            this.set('positionId', position.id);
            this.position = position;
        }
    }

    fetchAddress() {
        if (this.address &&
            !this.address.isNew() &&
            !this.address.hasChanged()
        ) {
            Promise.resolve(this.address);
        }

        if (!this.get('addressId')) {
            this.address = null;
            return Promise.resolve(null);
        }

        return new Address(
            {objectId: this.get('addressId')},
            {validate: false}
        ).fetch()
            .then(model => {
                this.address = model;
                return this.address;
            });
    }

    getAddress() {
        return this.address;
    }

    setAddress(address) {
        const old = this.getAddress();
        if (old) {
            old.off('sync', null, null);
        }

        this.set('addressId', address ? address.id : null);
        this.address = address;

        if (address) {
            this.address.on('sync', () => {
                this.set('addressId', this.address.id);
            }, this);
        }
    }

    fetchBankAccount() {
        if (this.bankAccount &&
            !this.bankAccount.isNew() &&
            !this.bankAccount.hasChanged()) {
            Promise.resolve(this.bankAccount);
        }

        if (!this.get('bankAccountId')) {
            this.bankAccount = null;
            return Promise.resolve(null);
        }

        return new BankAccount(
            {objectId: this.get('bankAccountId')},
            {validate: false}
        ).fetch()
            .then(model => {
                this.bankAccount = model;
                return this.bankAccount;
            })
            .catch(() => {
                this.bankAccount = null;
                return this.bankAccount;
            });
    }

    getBankAccount() {
        return this.bankAccount;
    }

    setBankAccount(bankAccount) {
        const old = this.getBankAccount();
        if (old) {
            old.off('sync', null, null);
        }

        this.set('bankAccountId', bankAccount ? bankAccount.id : null);
        this.bankAccount = bankAccount;

        if (bankAccount) {
            this.bankAccount.on('sync', () => {
                this.set('bankAccountId', this.bankAccount.id);
            }, this);
        }
    }

    fetchAttachments(tags = []) {
        const attachments = new Attachments(null, {attachable: this});

        return attachments.fetch({filter: {
            where: {
                tags: {inq: tags}
            },
            limit: 20
        }});
    }

    getRemovalReason() {
        if (!this.removal) {
            return '';
        }

        const reasons = {
            0: 'Select a reason',
            1: 'Terminated',
            2: 'Redundant',
            3: 'Resigned',
            4: 'Retired',
            5: 'Left Malaysia',
            6: 'Other'
        };

        return reasons[this.removal.get('reason')];
    }

    getRemovalDate() {
        if (!this.removal) {
            return '';
        }

        return moment(this.removal.get('removedAt')).format('DD.MM.YYYY');
    }

    invite() {
        return axios.post(
            this.finalUrl() + '/invite',
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).catch(err => {
            if (err.response) {
                throw err.response.data.error;
            }
            throw err;
        });
    }

    requestChanges(text) {
        return axios.post(
            this.finalUrl() + '/requestChanges',
            {text},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    save() {
        if (this.imageChanged) {
            this.imageChanged = false;
        }
        if (
            this.locationAdded && this.get('companyLocationId') &&
            !this.unsavedAttributes.companyLocationId
        ) {
            this.unsavedAttributes = Object.assign(
                this.unsavedAttributes,
                {companyLocationId: this.get('companyLocationId')}
            );
            this.locationAdded = false;
        }
        if (this.getPosition() && this.getPosition().isNew()) {

            return this.getPosition().save()
                .then(() => this.set(
                    {positionId: this.getPosition().id},
                    {validate: false}
                ))
                .then(() => super.save(...arguments));
        }

        return super.save(...arguments);
    }

    /**
     * Based on the earningsAmount and earningsFrequency fields we update
     * the associated models/Position only if it is a new position object.
     *
     * @instance
     * @private
     * @returns {null|undefined} Nothing
     */
    updatePositionInfo(model, value, options) {
        const attrsWhitelist = ['earningsAmount', 'earningsFrequency'];

        if (this.getPosition() && this.getPosition().isNew()) {
            _.each(_.keys(options.changes), (attr) => {
                if (!_.contains(attrsWhitelist, attr)) {
                    return;
                }

                this.getPosition().set(attr, value);
            });
        }
    }

    /**
     * If the ID type is not an IC disable socso category
     * if the ID Type is IC and isResident is true force
     * 12/13% employer contribution
     *
     * @instance
     * @private
     */
    onIdTypeChange(model, value) {
        if (!model.dirty()) {
            return;
        }

        if (value === consts.ID_TYPE_IC_NUMBER && model.get('isResident')) {
            this.set('epfEmployerRate', consts.EPF_EMPLOYER_RATE_GOVT_TABLE);
        }
    }

    updateSocsoCategory() {
        if (this.getAgeAtEndOfPeriod() < 60 &&
            (this.isMalaysian() || this.get('isPermanentResident'))
        ) {
            this.set('socsoCategory',
                consts.SOCSO_CATEGORY_INJURY_AND_INVALIDITY);
        } else {
            this.set('socsoCategory', consts.SOCSO_CATEGORY_INJURY);
        }
    }

    updateEpfRate() {
        const oldEpfRate = this.get('epfRate');
        const rates = this.getApplicableEpfRates();
        if (!rates.find(({value}) => value === oldEpfRate)) {
            const newRate = rates.find((i) => i.default);
            this.set('epfRate', newRate.value);
        }
    }

    onUpdate(model) {
        if (!model.dirty()) {
            return;
        }
        this.updateSocsoCategory();
        this.updateEpfRate();
    }

    isAffected2025Foreigner() {
        return !this.isMalaysian() &&
            !this.get('isPermanentResident') &&
            !this.get('isEpfMemberBefore1998') &&
            this.get('residentSubType') !== 3 &&
            !(this.isIntern === true && this.get('isEpf') !== true);
    }

    getApplicableEpfRates() {
        if (this.isAffected2025Foreigner()) {
            return [{
                text: '2%',
                value: 2,
                default: true
            }];
        }

        if (this.getAgeAtEndOfPeriod() >= 60) {
            return this.isMalaysian() ? [
                {
                    text: '0%',
                    value: 0,
                    default: true
                }
            ] : [
                {
                    text: '5.5%',
                    value: 0.055,
                    default: true
                }
            ];
        }

        return [
            {
                text: '9%',
                value: 0.09
            },
            {
                text: '11%',
                value: 0.11,
                default: true
            }
        ];
    }

    getEmployerEpfRate() {
        if (this.isAffected2025Foreigner()) {
            return {
                label: '2%',
                value: consts.EPF_EMPLOYER_RATE_GOVT_TABLE
            };
        }
        if (!this.isMalaysian() &&
            !this.get('isPermanentResident') &&
            !this.get('isEpfMemberBefore1998')) {
            return {
                label: '0% (RM 5)',
                value: consts.EPF_EMPLOYER_RATE_5RM
            };
        }
        return {
            label: this.getAgeAtEndOfPeriod() < 60 ?
                '12/13%' :
                this.isMalaysian() ? '4%' : '6/6.5%',
            value: consts.EPF_EMPLOYER_RATE_GOVT_TABLE
        };
    }

    /**
     * @instance
     * @returns {boolean}
     */
    isMale() {
        return this.get('gender') === consts.GENDER_MALE;
    }

    /**
     * @instance
     * @returns {boolean}
     */
    isFemale() {
        return this.get('gender') === consts.GENDER_FEMALE;
    }

    /**
     * Get the employee's company location
     *
     * @instance
     * @returns {string} company location
     */
    getCompanyLocationName() {
        return this.companyLocation ? this.companyLocation.get('name') : '';
    }

    /**
     * Get the employee's position
     *
     * @instance
     * @returns {string} position
     */
    getPositionName() {
        return this.position ? this.position.get('name') : '';
    }

    /**
     * Get the employee's age
     *
     * @instance
     * @returns {integer} age
     */
    getAge() {
        return moment().diff(this.get('dob') || new Date(), 'years');
    }

    /**
     * Get the employee's age at the end of the current payroll period
     *
     * @instance
     * @returns {integer} age
     */
    getAgeAtEndOfPeriod() {
        return new Payroll().getPeriod().endOf('month')
            .diff(this.get('dob'), 'years');
    }

    /**
     * Get the employee's DOB
     *
     * @instance
     * @returns {string} DOB
     */
    getDob() {
        const date = this.get('dob');
        return date ? moment(date).format('DD/MM/YYYY') : '';
    }

    /**
     * Get race label or string
     *
     * @instance
     * @returns {string} The race name
     */
    getRace() {
        const type = {};
        type[consts.RACE_DEFAULT] = 'Not selected';
        type[consts.RACE_MALAY] = 'Malay';
        type[consts.RACE_CHINESE] = 'Chinese';
        type[consts.RACE_INDIAN] = 'Indian';
        type[consts.RACE_OTHER] = 'Other';
        return type[this.get('race')];
    }

    /**
     * Get residence type string.
     *
     * @instance
     * @returns {string} The residence type
     */
    getResidentType() {
        const type = {};
        type[consts.RESIDENT_SUBTYPE_NORMAL] = 'Normal';
        type[consts.RESIDENT_SUBTYPE_KNOWLEDGE_WORKER] =
            'Iskandar Knowledge Worker';
        type[consts.RESIDENT_SUBTYPE_REP] = 'Returning Expert Program';
        return type[this.get('residentSubType')];
    }

    /**
     * Get marital status string
     *
     * @instance
     * @returns {string} The marital status as string
     */
    getMarriedStatus() {
        const type = {};
        type[consts.MARRIED_STATUS_SINGLE] = 'Single';
        type[consts.MARRIED_STATUS_SINGLE_WITH_CHILDREN] =
            'Single with Children';
        type[consts.MARRIED_STATUS_DIVORCED] =
            'Divorced or Widowed';
        type[consts.MARRIED_STATUS_SPOUSE_WORKING] =
            'Married and spouse is working';
        type[consts.MARRIED_STATUS_SPOUSE_NOT_WORKING] =
            'Married and spouse is NOT working';

        return type[this.get('marriedStatus')];
    }

    /**
     * Get preferred payment method as string
     *
     * @instance
     * @returns {string} The payment method
     */
    getPreferredPaymentMethod() {
        const type = {};
        type[consts.PAYMENT_METHOD_DEFAULT] = '';
        type[consts.PAYMENT_METHOD_BANK_TRANSFER] = 'Bank Transfer';
        type[consts.PAYMENT_METHOD_CASH] = 'Cash';
        type[consts.PAYMENT_METHOD_CHEQUE] = 'Cheque';
        return type[this.get('preferredPaymentMethod')];
    }

    /**
     * Get children as string
     *
     * @instance
     * @returns {string} The children
     */
    getChildren() {
        const type = {
            0: 'None of the Categories',
            underage: 'Under 18 or In Education',
            inEducation: 'Over 18 - In Education (Diploma level)',
            inHigherEducation:
                'Over 18 - In Education (Degree level or higher)',
            disabled: 'Disabled',
            disabledInEducation: 'Disabled - In Higher Education'
        };
        const res = [];
        const groupedChildren = _.groupBy(this.get('children'), item => {
            const category = item ? type[item.category] : type[0];
            return category + ' (' + item.pcbDeduction + '%)';
        });

        _.each(groupedChildren, (item, key) => {
            res.push(item.length + ' ' + key);
        });

        return res;
    }

    /**
     * Get nationality as string. Not as country code
     *
     * @instance
     * @returns {string} The nationality
     */
    getNationality() {
        return _.find(countriesList, {code: this.get('nationality')}).name;
    }

    isMalaysian() {
        return this.get('nationality') === 'MY';
    }

    /**
     * Get how many children the employee has, regardless of type or
     * classification
     *
     * @instance
     * @returns {integer} The employee's children count
     */
    getChildrenCount() {
        return (this.get('children') || []).length;
    }

    /**
     * @instance
     * @return {integer} integer in range 0...100
     */
    calculateProfileProgress() {
        const populatedFieldCount = this.fieldsAffectingProgress.reduce(
            (memo, field) => {
                if (this.get(field)) {
                    memo++;
                }
                return memo;
            }, 0);
        return Math.floor(
            populatedFieldCount / this.fieldsAffectingProgress.length * 100
        );
    }

    getNameAbbreviation() {
        const name = this.get('fullName') || '';
        const words = name.match(/(\w+)/g) || '';

        if (!name) {
            return '';
        }
        if (!words) {
            return '';
        }
        if (words.length === 1) {
            return words[0].substr(0, 2);
        }
        return words[0].charAt(0) + words.pop().charAt(0);
    }

    getTaxModifyingAttributes() {
        const r = [];

        if (this.get('isDisabled')) {
            r.push('Disabled');
        }

        if (!this.get('isResident')) {
            r.push('Non-Resident');
        } else {
            r.push('Resident');

            if (
                this.get('residentSubType') ===
                    consts.RESIDENT_SUBTYPE_KNOWLEDGE_WORKER
            ) {
                r.push('Iskandar Knowledge Worker');
            } else if (
                this.get('residentSubType') ===
                    consts.RESIDENT_SUBTYPE_REP
            ) {
                r.push('Returning Expert Program');
            }
        }

        switch (this.get('marriedStatus')) {
            case consts.MARRIED_STATUS_SINGLE:
                r.push('Single');
                break;
            case consts.MARRIED_STATUS_SPOUSE_NOT_WORKING:
                r.push('Married');
                r.push('Spouse Not Working');
                break;
            case consts.MARRIED_STATUS_SPOUSE_WORKING:
                r.push('Married');
                r.push('Spouse Working');
                break;
            case consts.MARRIED_STATUS_DIVORCED:
                r.push('Divorced');
                break;
            case consts.MARRIED_STATUS_SINGLE_WITH_CHILDREN:
                r.push('Single W/ Children');
                break;
        }

        if (this.get('isSpouseDisabled')) {
            r.push('Married');
            r.push('Spouse Disabled');
        }

        if (this.get('marriedStatus') !== consts.MARRIED_STATUS_SINGLE) {
            if (this.getChildrenCount() === 1) {
                r.push('1 Child: ' + this.getChildren().join(', '));
            } else if (this.getChildrenCount() > 1) {
                r.push(this.getChildrenCount() +
                    ' Children: ' + this.getChildren().join(', '));
            } else {
                r.push('No Dependent Children');
            }
        }

        return r;
    }

    generateFormEA(period) {
        return axios.post(
            `${FORMS_URL}/${this.id}/generateFormEA`,
            {period: period},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    generateFormPcb2(period) {
        return axios.post(
            `${FORMS_URL}/${this.id}/generateFormPcb2`,
            {period: period},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    generateFormExcel(period) {
        return axios.post(
            `${FORMS_URL}/${this.id}/generateExcel`,
            {period: period},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    getApplicableLeavePeriodDates(date) {
        if (!this.company) {
            return {
                start: moment(date).startOf('year'),
                end: moment(date).endOf('year')
            };
        }

        const joinDate = moment(
            this.get('commencementDate') || date
        ).startOf('day');

        if (this.company.get('leaveRenewalPolicy') === 'joinDate') {
            if (moment(date).isBefore(joinDate, 'day')) {
                return {
                    start: moment(joinDate).subtract(1, 'year').startOf('day'),
                    end: moment(joinDate).subtract(1, 'day').endOf('day')
                };
            }
            const years = moment(date).startOf('day').diff(joinDate, 'years');

            const start = moment(joinDate).add(years, 'years');

            return {
                start,
                end: moment(start).add(1, 'year')
                    .subtract(1, 'day').endOf('day')
            };
        }

        let start = moment(date).startOf('year');

        // Change starting date to joining date for
        // "Renews at the beginning of the year" + employee joined this year
        if (
            this.company.get('leaveRenewalPolicy') === 'calendarYear' &&
            moment(date).isSame(joinDate, 'year')
        ) {
            start = moment(joinDate);
        }

        return {
            start,
            end: moment(date).endOf('year')
        };
    }

    getApplicableLeavePeriod(date) {
        return this.getApplicableLeavePeriodDates(date).start.year();
    }

    getDaysEntitled(type, period) {
        if (!period) {
            period = this.getApplicableLeavePeriod(moment());
        }

        const daysEntitled = this.get('daysEntitled') || {};
        const daysEntitledForPeriod = daysEntitled[period] ||
            daysEntitled[period - 1] || {};

        let defaultDaysEntitled = 0;
        if (this.company) {
            defaultDaysEntitled = this.company.getLeaveDays(type);
        }

        return daysEntitledForPeriod[type.id] !== undefined ?
            parseFloat(daysEntitledForPeriod[type.id]) :
            defaultDaysEntitled;
    }

    dismiss({
        reason,
        isReported,
        removedAt,
        notes,
        prorateSalary,
        personalEmail
    }) {
        return axios.post(this.finalUrl() + '/dismiss', {
            reason,
            isReported,
            removedAt,
            notes,
            prorateSalary,
            personalEmail
        }, {headers: {'Authorization': app.user.getAccessToken().id}});
    }

    restore() {
        return axios.delete(this.finalUrl() + '/dismissal', {
            headers: {'Authorization': app.user.getAccessToken().id}
        }).then(() => {
            return this.save({hiddenAt: null}, {validate: false});
        });
    }

    permanentDestroy() {
        return axios.post(this.finalUrl() + '/permanentDestroy', {}, {
            headers: {'Authorization': app.user.getAccessToken().id}
        });
    }

    // destroy user for the employee and unset userId
    removeEmployeeUser() {
        const userId = this.get('userId');
        const user = new User({id: userId});

        return user.remove()
            .then(() => this.save({userId: null}, {validate: false}));
    }

    /**
     * period should be instanceof moment
     */
    getHourlyWage(date, name = 'overtime') {
        return axios.get(this.finalUrl() + '/hourlyWage', {
            //date is wrapped by moment because some parts of the app
            //might send moment instances or date objects
            params: {
                name,
                date: moment(date).toDate()
            },
            headers: {'Authorization': app.user.getAccessToken().id}
        }).then(r => r.data);
    }

    setDaysEntitled({type, typeId, days, period}) {
        if (!typeId) {
            typeId = type.id;
        }
        if (!period) {
            period = this.getApplicableLeavePeriod(moment());
        }

        const daysEntitled = this.get('daysEntitled') || {[period]: {}};
        if (!daysEntitled[period]) {
            daysEntitled[period] = JSON.parse(JSON.stringify(
                daysEntitled[period - 1] || this.company.get('leaveDays') || {}
            ));
        }
        daysEntitled[period][typeId] = parseFloat(days);
        this.set('daysEntitled', daysEntitled);
    }

    /* there isn't a getDaysCarryForward method because it's a complex
       calculation made in server side */
    setDaysCarryForward({typeId, days}) {
        const period = this.getApplicableLeavePeriod(moment());
        const daysCarryForward =
            this.get('daysCarryForward') || {[period]: {}};
        if (!daysCarryForward[period]) {
            daysCarryForward[period] = {};
        }
        daysCarryForward[period][typeId] = parseFloat(days);

        this.set('daysCarryForward', daysCarryForward);
    }

    fetchLeaveApprovals() {
        return this.leaveApprovals.fetch({filter: {limit: 9999}})
            .then(() => this.leaveApprovals);
    }

    isManagedBy(manager) {
        if (!this.leaveApprovals) {
            return false;
        }
        return this.leaveApprovals.length > 0 &&
            this.leaveApprovals
                .map(a => a.get('approverId'))
                .indexOf(manager.id) > -1;
    }

    getApprovalLayer(manager) {
        if (!this.leaveApprovals) {
            return -1;
        }
        const appr = this.leaveApprovals.find(
            a => a.get('approverId') === manager.id
        );
        if (!appr) {
            return -1;
        }
        return appr.get('layer');
    }

    createTimeEntry(date, item = null) {
        return new TimeEntry({
            employeeId: this.get('objectId'),
            date: date,
            hours: 0,
            payrollItemId: item && item.get('objectId')
        });
    }

    getTimeEntry(date, item = null, insert = true) {
        if (!this._timeEntries) {
            throw new Error(`We don't have time entry information for this model`);
        }

        const commencementDate = this.get('commencementDate') ?
            moment(this.get('commencementDate')).startOf('day') :
            moment(date);
        const cessationDate = this.get('cessationDate') ?
            moment(this.get('cessationDate')).startOf('day') :
            null;
        const isBeforeCommencementDate =
            moment(date).startOf('day').isBefore(commencementDate);
        const isAfterCessationDate = cessationDate ?
            moment(date).startOf('day').isAfter(cessationDate) : false;

        if (isBeforeCommencementDate || isAfterCessationDate) {
            return false;
        }

        const entry = this._timeEntries.find(entry => {
            const itemId = item && item.id;
            const entryItemId = entry.get('payrollItemId');
            const isNotMonthly = !entry.get('isMonthOnly');

            return (
                isNotMonthly &&
                moment(date).isSame(new Date(entry.get('date')), 'day') && (
                    (!item && !entry.get('payrollItemId')) ||
                    itemId === entryItemId
                )
            );
        });

        if (entry) {
            return entry;
        }

        if (insert) {
            const newEntry = this.createTimeEntry(date, item);
            this._timeEntries.add(newEntry);
            return newEntry;
        }
    }

    getVisiblePayrollItems() {
        return app.payrollItems.filter(model =>
            (model.get('hiddenAt') === null && this._timeEntries
                .some(entry => entry.get('payrollItemId') === model.id))
        );
    }

    getTimeEntryItems() {
        if (app.payrollItems) {
            return [null, ...this.getVisiblePayrollItems()];
        }
        log.warn(`App hasn't any payroll items loaded`);
    }

    getTimeEntriesDailyTotal(date) {
        const entries = this._timeEntries.filter(entry => {
            return moment(entry.get('date')).isSame(date, 'day') &&
                !entry.get('isMonthOnly');
        });
        if (entries.length === 0) {
            entries.push(this.getTimeEntry(date));
        }

        return entries.reduce((m, entry) => {
            return m + ((entry && entry.get('hours')) || 0);
        }, 0);
    }

    getTimeEntriesWeeklyTotal(date, item = null) {
        const weekStart = moment(date).startOf('week');
        const weekEnd = moment(date).endOf('week');
        let total = 0;

        while (!weekStart.isAfter(weekEnd)) {
            const entry = this.getTimeEntry(weekStart.toDate(), item, false);
            total += entry ? (entry.get('hours') || 0) : 0;
            weekStart.add(1, 'day');
        }
        return total;
    }

    getTimeEntriesMonthlyTotal(date, item = null) {
        const monthStart = moment(date).startOf('month');
        const monthEnd = moment(date).endOf('month');
        let total = 0;

        while (!monthStart.isAfter(monthEnd)) {
            const entry = this.getTimeEntry(monthStart.toDate(), item, false);
            total += entry ? ((entry && entry.get('hours')) || 0) : 0;
            monthStart.add(1, 'day');
        }

        // count monthly time entries from bulk upload
        const entry = this._timeEntries.find(entry => {
            const itemId = item && item.id;
            const entryItemId = entry.get('payrollItemId');
            const isMonthly = entry.get('isMonthOnly');

            return (
                isMonthly &&
                moment(monthEnd)
                    .isSame(new Date(entry.get('date')), 'day') && (
                    (!itemId && !entry.get('payrollItemId')) ||
                        itemId === entryItemId
                )
            );
        });

        total += entry ? ((entry && entry.get('hours')) || 0) : 0;

        return total;
    }

    deleteTimeEntries(date, item = null, scope = 'day') {
        if (!this._timeEntries) {
            throw new Error(`We don't have time entry information for this model`);
        }

        const start = moment(date).startOf(scope);
        const end = moment(date).endOf(scope);

        const promises = [];
        while (!start.isAfter(end)) {
            const entry = this.getTimeEntry(start.toDate(), item, false);
            if (entry) {
                this._timeEntries.remove(entry);
                promises.push(entry.destroy());
            }
            start.add(1, 'day');
        }
        if (scope === 'month') {
            const monthEntry = this._timeEntries.find(
                (i) => i.get('isMonthOnly') &&
                    i.get('payrollItemId') === item.id
            );

            if (monthEntry) {
                this._timeEntries.remove(monthEntry);
                promises.push(monthEntry.destroy());
            }
        }
        return Promise.all(promises);
    }

    getFormattedDate(property, format) {
        const date = this.get(property);
        return date ? moment(date).format(format) : '';
    }

    canContributeToEis() {
        const myNationality = (this.get('nationality')) === 'MY';
        return (
            myNationality || this.get('isPermanentResident')
        );
    }

    // 20200819 this.hasPrevItemYearlyTotals: to determine whether
    // an employee has entered YTD data. this is for warning dialog
    // at payroll step 1 (when clicking step 2)
    getHasPrevItemYearlyTotals(year) {
        return new ItemYearlyTotals(null, {
            employee: this, year: year || moment().year()
        }).fetch({filter: {limit: 999}})
            .then(itemYearlyTotals => {
                this.hasPrevItemYearlyTotals = itemYearlyTotals.some(
                    ytd => ytd.get('amount') > 0 ||
                ytd.get('prevEmploymentAmount') > 0
                );
            });
    }

    isDestroyable() {
        const payslips = new Payslips(null, {
            employee: this,
            filter: {limit: 1},
            pagination: false
        });
        return this.fetchCompany().then(company => {
            return Promise.all([
                company.fetchCurrentPayroll(),
                payslips.fetch()
            ]).then(([payroll, payslip]) => {
                return (!payroll && !payslip.length);
            })
        })
    }

    autoFillPreviousPayslipsData({
        itemYearlyTotals,
        current = true
    }) {
        if (itemYearlyTotals.length < 1) {
            return Promise.resolve(itemYearlyTotals);
        }
        return axios.post(
            `${this.finalUrl()}/autoFillPreviousPayslipsData`,
            {
                current: current,
                itemYearlyTotals: itemYearlyTotals.models.map(model => {
                    return model.toJSON();
                })
            },
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(res => itemYearlyTotals.reset(res.data.results));
    }

    uploadImage(file = null) {
        if (!file) {
            return Promise.resolve();
        }

        // Size limit: 200 kb
        if (file && file.size && file.size > 1024 * 200) {
            return Promise.reject({text: `The maximum allowed size is 200kb`});
        }

        if (!/^image\/(png|jpeg)$/.test(file.type)) {
            return Promise.reject({
                text: `Only PNG or JPEG images are allowed`
            });
        }

        const extension = file.name.split('.').pop();
        const data = new FormData();
        data.append(`file`, file, `image.${extension}`);

        // Hack to cover `unsavedAttributes` issue
        const pendingChanges = Object.assign({}, this.unsavedAttributes || {});

        return axios.post(
            `${this.finalUrl()}/image`,
            data,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch({validate: false})
        ).then(() => {
            // tracking of `unsavedAtrributes` is working incorrectly as
            // backbone appends server sent attributes into model without reset
            this.unsavedAttributes = pendingChanges;
            this.set(pendingChanges);
            this.imageChanged = true;
        });
    }

    getImageUrl() {
        if (!this.imageChanged && !this.get('imageUrl')) {
            return false;
        }
        const tempParam = this.imageChanged ? '&displayTemp=1' : '';

        return `${this.finalUrl()}/image?` +
            `access_token=${app.user.getAccessToken().id}&` +
            `_b=${Math.floor(Math.random() * 1000000)}` +
            tempParam;
    }
}



// WEBPACK FOOTER //
// ./app/new-models/employee.js